import { useEffect, useRef } from 'react';

/**
 * Returns a ref that makes an `overflow-x: auto` element scroll
 * horizontally in response to wheel/trackpad gestures.
 *
 * This is needed when the element is nested inside another scroll container
 * (e.g. FPL's `Modal.Body`, whose scroll div is `overflow-x: hidden` with
 * `overscroll-behavior: none`). In that setup the browser latches a horizontal
 * gesture onto the ancestor scroller and the inner element never scrolls.
 *
 * A non-passive `wheel` listener is attached so we can `preventDefault()` and
 * claim the gesture before the ancestor does, then apply the delta to
 * `scrollLeft` ourselves. React's `onWheel` is registered as passive and cannot
 * `preventDefault()`, so it does not work for this case.
 *
 * The gesture is released at the start/end edges so the surrounding scroll
 * container can keep scrolling vertically once the element is fully scrolled.
 *
 * @example
 * const carouselRef = useHorizontalWheelScroll();
 * return <div ref={carouselRef} style={{ overflowX: 'auto' }}>...</div>;
 */
export const useHorizontalWheelScroll = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;

      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

      el.scrollLeft += delta;
      e.preventDefault();
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);
  return ref;
};
